import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Loader2,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { baixarMidia } from './baixar';
import type { ItemMidia } from './arquivo';

function dataHora(ts?: number) {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function BotaoTopo({
  onClick,
  label,
  children,
  disabled,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="flex size-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/**
 * Visualizador em tela cheia das mídias da conversa (imagem, vídeo, PDF).
 * Setas do teclado navegam, Esc fecha, clique na imagem amplia.
 */
export function MediaViewer({
  itens,
  indiceInicial,
  onFechar,
}: {
  itens: ItemMidia[];
  indiceInicial: number;
  onFechar: () => void;
}) {
  const [indice, setIndice] = useState(indiceInicial);
  const [zoom, setZoom] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [baixando, setBaixando] = useState(false);
  const fecharRef = useRef<HTMLButtonElement>(null);
  const item = itens[indice];

  const ir = useCallback(
    (delta: number) => {
      setIndice((i) => Math.min(itens.length - 1, Math.max(0, i + delta)));
    },
    [itens.length]
  );

  // Trocou de item: volta ao tamanho normal e mostra o carregando de novo.
  useEffect(() => {
    setZoom(false);
    setCarregando(true);
  }, [indice]);

  useEffect(() => {
    fecharRef.current?.focus();
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
      else if (e.key === 'ArrowRight') ir(1);
      else if (e.key === 'ArrowLeft') ir(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = anterior;
    };
  }, [ir, onFechar]);

  if (!item) return null;

  const baixar = async () => {
    setBaixando(true);
    await baixarMidia(item.messageId, item.nome, item.url);
    setBaixando(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Visualizar ${item.nome}`}
      className="fixed inset-0 z-[150] flex flex-col bg-black"
    >
      {/* Topo: de quem, quando, ações */}
      <div className="flex items-center gap-3 px-4 py-3 text-white">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{item.nome}</p>
          <p className="truncate text-xs text-white/60">
            {item.deQuem}
            {item.quando ? ` · ${dataHora(item.quando)}` : ''}
          </p>
        </div>
        {item.tipo === 'imagem' && (
          <BotaoTopo onClick={() => setZoom((z) => !z)} label={zoom ? 'Reduzir' : 'Ampliar'}>
            {zoom ? <ZoomOut size={19} /> : <ZoomIn size={19} />}
          </BotaoTopo>
        )}
        <BotaoTopo onClick={baixar} label="Baixar" disabled={baixando}>
          {baixando ? <Loader2 size={19} className="animate-spin" /> : <Download size={19} />}
        </BotaoTopo>
        <BotaoTopo
          onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
          label="Abrir em nova aba"
        >
          <ExternalLink size={19} />
        </BotaoTopo>
        <button
          ref={fecharRef}
          type="button"
          onClick={onFechar}
          aria-label="Fechar"
          title="Fechar (Esc)"
          className="flex size-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X size={21} />
        </button>
      </div>

      {/* Conteúdo */}
      <div
        className={cn(
          'relative flex min-h-0 flex-1 items-center justify-center px-14',
          zoom ? 'overflow-auto' : 'overflow-hidden'
        )}
        onClick={(e) => {
          if (e.target === e.currentTarget) onFechar();
        }}
      >
        {carregando && item.tipo === 'imagem' && (
          <Loader2 size={28} className="absolute animate-spin text-white/60" />
        )}
        {item.tipo === 'imagem' && (
          <img
            key={item.chave}
            src={item.url}
            alt={item.legenda || item.nome}
            onLoad={() => setCarregando(false)}
            onError={() => setCarregando(false)}
            onClick={() => setZoom((z) => !z)}
            className={cn(
              'select-none rounded-lg transition-transform',
              zoom
                ? 'max-w-none cursor-zoom-out'
                : 'max-h-full max-w-full cursor-zoom-in object-contain'
            )}
            style={zoom ? { width: '180%' } : undefined}
          />
        )}
        {item.tipo === 'video' && (
          <video
            key={item.chave}
            src={item.url}
            controls
            autoPlay
            playsInline
            className="max-h-full max-w-full rounded-lg bg-black"
          />
        )}
        {item.tipo === 'pdf' && (
          // <object> em vez de <iframe>: se o navegador não tiver leitor de PDF,
          // mostra o conteúdo de dentro (aviso + abrir/baixar) em vez de um
          // quadro cinza vazio.
          <object
            key={item.chave}
            data={item.url}
            type="application/pdf"
            aria-label={item.nome}
            onLoad={() => setCarregando(false)}
            className="h-full w-full max-w-5xl rounded-lg bg-white"
          >
            <div className="flex h-full flex-col items-center justify-center gap-3 rounded-lg bg-neutral-900 p-6 text-center text-white/80">
              <p className="text-sm">Este navegador não mostra PDF aqui dentro.</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')}
                  className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20"
                >
                  <ExternalLink size={14} /> Abrir em nova aba
                </button>
                <button
                  type="button"
                  onClick={baixar}
                  className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20"
                >
                  <Download size={14} /> Baixar
                </button>
              </div>
            </div>
          </object>
        )}

        {itens.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => ir(-1)}
              disabled={indice === 0}
              aria-label="Anterior"
              className="absolute left-3 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 disabled:opacity-0"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              type="button"
              onClick={() => ir(1)}
              disabled={indice === itens.length - 1}
              aria-label="Próxima"
              className="absolute right-3 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 disabled:opacity-0"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      {/* Rodapé: legenda e posição */}
      <div className="px-4 pb-4 pt-2 text-center">
        {item.legenda && (
          <p className="mx-auto mb-2 line-clamp-3 max-w-2xl whitespace-pre-wrap text-sm text-white/90">
            {item.legenda}
          </p>
        )}
        {itens.length > 1 && (
          <p className="text-xs tabular-nums text-white/50">
            {indice + 1} de {itens.length}
          </p>
        )}
      </div>
    </div>
  );
}
